/**
 * consoleNav — the in-memory client-side navigation channel (F08).
 *
 * The console has no router and no shared store: ConsoleShell holds the active
 * screen in local useState and tabs are zero-prop and self-fetching. This tiny
 * module-level pub/sub is the only mechanism by which a zero-prop tab (the
 * Guide) asks ConsoleShell to switch screens and hands the Submit screen a
 * one-shot prefill payload, without adding a router or a global store.
 *
 * Deliberately in-memory, NOT sessionStorage: sessionStorage survives reload,
 * so a stale prefill or screen-switch could re-fire unexpectedly. An in-memory
 * channel is synchronous and naturally one-shot (the prefill is cleared on
 * read, and listeners only fire while the shell is mounted).
 */

let listeners = new Set();
let pendingPrefill = null;

/**
 * Ask ConsoleShell to switch to `screenId`. If a `payload` is supplied it is
 * stored as a one-shot prefill for the target screen to consume on mount.
 */
export function requestScreen(screenId, payload = null) {
  if (payload != null) pendingPrefill = payload;
  listeners.forEach((fn) => fn(screenId));
}

/** Subscribe to screen-switch requests. Returns an unsubscribe function. */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Read and clear the pending one-shot prefill, normalised to an envelope.
 *
 * Returns `{ fields, meta }` (once) or null. `requestScreen` stores its payload
 * opaquely; this is the single place that gives it shape, so callers always see
 * the same structure regardless of how it was queued:
 *   - envelope in (`{ fields, meta }`) -> passed through (meta defaults to {})
 *   - bare job-fields object in (F08 back-compat) -> `{ fields: <obj>, meta: {} }`
 *   - null -> null
 *
 * The envelope (F09 FR3) keeps demo metadata (`meta.demo`) separate from the
 * form fields so the marker is never spread into form state or the job payload.
 * Still one-shot: the very next call returns null until a new requestScreen
 * carries a payload.
 */
export function consumePrefill() {
  const p = pendingPrefill;
  pendingPrefill = null;
  if (p == null) return null;
  // Envelope shape: an object carrying a `fields` key. Anything else is a bare
  // F08-era job-fields object — wrap it with empty meta for back-compat.
  if (typeof p === "object" && "fields" in p) {
    return { fields: p.fields ?? {}, meta: p.meta ?? {} };
  }
  return { fields: p, meta: {} };
}
